import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useBillingMoney } from "../hooks";
import type { BilledBuilding, BillingOverview } from "../types";
import { BilledBuildingRow } from "./BilledBuildingRow";

interface BillingTableProps {
  overview: BillingOverview;
  removeDisabledReason?: string;
  chooseForRemoval: (building: BilledBuilding) => void;
}

export function BillingTable({
  overview,
  removeDisabledReason,
  chooseForRemoval,
}: BillingTableProps) {
  const { t } = useLanguage();
  const money = useBillingMoney();
  const copy = t.billing.table;

  return (
    <Table className="table-fixed border-separate border-spacing-y-2">
      <colgroup>
        <col className="w-[23%] lg:w-[28%]" />
        <col className="w-[34%] lg:w-[29%]" />
        <col className="w-[24%]" />
        <col />
        <col className="w-12" />
      </colgroup>
      <TableHeader className="[&_th]:px-2 lg:[&_th]:px-4 [&_tr]:border-0">
        <TableRow className="hover:bg-transparent">
          <TableHead className="font-medium tracking-wider">{copy.building}</TableHead>
          <TableHead className="font-medium tracking-wider">{t.billing.plans.plan}</TableHead>
          <TableHead className="font-medium tracking-wider">{copy.servicePeriod}</TableHead>
          <TableHead className="text-right font-medium tracking-wider">{copy.amount}</TableHead>
          <TableHead>
            <span className="sr-only">{copy.action}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody className="[&_tr:last-child]:border [&_tr:last-child]:border-[#ececed]">
        {overview.buildings.map((building) => (
          <BilledBuildingRow
            key={building.building_id}
            building={building}
            currency={overview.context.currency}
            context={overview.context}
            removeDisabledReason={removeDisabledReason}
            chooseForRemoval={chooseForRemoval}
          />
        ))}
      </TableBody>
      <TableFooter className="border-0 bg-transparent [&_td]:px-2 lg:[&_td]:px-4">
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={3}>
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-[#262626]">{copy.footerTotal}</span>
            </div>
          </TableCell>
          <TableCell className="text-right">
            {money(overview.estimated_total_cents, overview.context.currency)}
          </TableCell>
          <TableCell />
        </TableRow>
      </TableFooter>
    </Table>
  );
}
